/**
 * AdminDashboard Page
 *
 * Main admin dashboard with platform metrics and activity overview.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAdminMetrics, DateRange } from './hooks';
import { MetricsCards, ActivityFeed, DateRangeFilter } from './components';
import { AdminLayout } from './layout';

export const AdminDashboard: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>('30days');
  const { metrics, isLoading, refetch } = useAdminMetrics(dateRange);

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-sm text-gray-500 mt-1">
              Platform overview and key metrics
            </p>
          </div>
          <div className="flex items-center gap-3">
            <DateRangeFilter value={dateRange} onChange={setDateRange} />
            <button
              onClick={refetch}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Refresh data"
            >
              <RefreshIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Metrics Cards */}
        {isLoading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl shadow-sm p-5 animate-pulse">
                <div className="flex items-center justify-between mb-3">
                  <div className="h-4 bg-gray-200 rounded w-20" />
                  <div className="w-9 h-9 bg-gray-200 rounded-lg" />
                </div>
                <div className="h-8 bg-gray-200 rounded w-16 mb-1" />
                <div className="h-3 bg-gray-200 rounded w-24" />
              </div>
            ))}
          </div>
        ) : metrics ? (
          <MetricsCards metrics={metrics} />
        ) : null}

        {/* Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Activity Feed */}
          <div className="lg:col-span-2">
            <ActivityFeed limit={10} />
          </div>

          {/* Quick Actions & Stats */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="font-semibold text-gray-900 mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <QuickActionLink
                  to="/admin/listings?status=pending"
                  label="Review pending listings"
                  count={metrics?.listings.pending}
                />
                <QuickActionLink
                  to="/admin/payouts?status=requested"
                  label="Process payouts"
                  count={metrics?.payouts.pending}
                  alert={metrics?.payouts.pending ? metrics.payouts.pending > 0 : false}
                />
                <QuickActionLink
                  to="/admin/users"
                  label="Manage users"
                />
                <QuickActionLink
                  to="/admin/bookings"
                  label="View bookings"
                />
                <QuickActionLink
                  to="/admin/reports"
                  label="Generate reports"
                />
              </div>
            </div>

            {/* Breakdown Stats */}
            {metrics && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Users by Role</h2>
                <div className="space-y-3">
                  <StatBar
                    label="Guests"
                    value={metrics.users.byRole.guest}
                    total={metrics.users.total}
                    color="bg-blue-500"
                  />
                  <StatBar
                    label="Hosts"
                    value={metrics.users.byRole.host}
                    total={metrics.users.total}
                    color="bg-green-500"
                  />
                  <StatBar
                    label="Admins"
                    value={metrics.users.byRole.admin}
                    total={metrics.users.total}
                    color="bg-purple-500"
                  />
                </div>
              </div>
            )}

            {/* Booking Stats */}
            {metrics && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="font-semibold text-gray-900 mb-4">Booking Status</h2>
                <div className="space-y-3">
                  <StatBar
                    label="Completed"
                    value={metrics.bookings.completed}
                    total={metrics.bookings.total}
                    color="bg-green-500"
                  />
                  <StatBar
                    label="Confirmed"
                    value={metrics.bookings.confirmed}
                    total={metrics.bookings.total}
                    color="bg-blue-500"
                  />
                  <StatBar
                    label="Pending"
                    value={metrics.bookings.pending}
                    total={metrics.bookings.total}
                    color="bg-yellow-500"
                  />
                  <StatBar
                    label="Cancelled"
                    value={metrics.bookings.cancelled}
                    total={metrics.bookings.total}
                    color="bg-red-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Cancellation rate: {metrics.bookings.cancelledRate.toFixed(1)}%
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Top Listings by Location */}
        {metrics && (
          <div className="bg-white rounded-xl shadow-sm p-6">
            <h2 className="font-semibold text-gray-900 mb-4">Listings by Location</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
              {Object.entries(metrics.listings.byLocation).map(([location, count]) => (
                <div key={location} className="text-center p-4 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">{count}</p>
                  <p className="text-sm text-gray-500">{location}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

// Quick action link component
interface QuickActionLinkProps {
  to: string;
  label: string;
  count?: number;
  alert?: boolean;
}

const QuickActionLink: React.FC<QuickActionLinkProps> = ({
  to,
  label,
  count,
  alert,
}) => (
  <Link
    to={to}
    className="flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
  >
    <span className="text-sm text-gray-700">{label}</span>
    {count !== undefined && count > 0 && (
      <span
        className={`text-xs px-2 py-0.5 rounded-full ${
          alert ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'
        }`}
      >
        {count}
      </span>
    )}
  </Link>
);

// Stat bar component
interface StatBarProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

const StatBar: React.FC<StatBarProps> = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-gray-600">{label}</span>
        <span className="text-gray-900 font-medium">{value.toLocaleString()}</span>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-500`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Refresh icon
const RefreshIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

export default AdminDashboard;
